import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Activity, Brain, Target, CheckCircle2, Shield, MessageSquare, Calendar, BookOpen, Home, User, Download, Zap, Dumbbell, Heart, Moon, Sparkles, Search, ArrowLeftRight } from 'lucide-react';
import ScheduleModal from '../components/ScheduleModal';
import ContactModal from '../components/ContactModal';
import LearnMoreModal from '../components/LearnMoreModal';
import TestimonialsSection from '../components/TestimonialsSection';
import ExitIntentPopup from '../components/ExitIntentPopup';

const GOAL_CARDS = [
  { id: 'weight-loss', icon: Dumbbell, title: 'Weight Loss', desc: 'Semaglutide, Tirzepatide, and compounded GLP-1 programs', color: 'from-emerald-500/20 to-green-500/10 border-emerald-500/20 hover:border-emerald-500/40' },
  { id: 'energy', icon: Zap, title: 'Energy & Cellular Health', desc: 'NAD+, MOTS-c, and mitochondrial support', color: 'from-cyan-500/20 to-blue-500/10 border-cyan-500/20 hover:border-cyan-500/40' },
  { id: 'recovery', icon: Activity, title: 'Recovery & Healing', desc: 'Peptide protocols for targeted recovery, tissue support, and full-body healing optimization', color: 'from-orange-500/20 to-amber-500/10 border-orange-500/20 hover:border-orange-500/40' },
  { id: 'muscle', icon: Target, title: 'Body Composition', desc: 'Tesamorelin, Sermorix, and muscle optimization', color: 'from-purple-500/20 to-violet-500/10 border-purple-500/20 hover:border-purple-500/40' },
  { id: 'cognitive', icon: Brain, title: 'Cognitive Performance', desc: 'NAD+, MOTS-c, and mitochondrial support for mental clarity', color: 'from-pink-500/20 to-rose-500/10 border-pink-500/20 hover:border-pink-500/40' },
  { id: 'sleep', icon: Moon, title: 'Sleep Optimization', desc: 'NAD+, Sermorelin, and sleep-supportive protocols', color: 'from-indigo-500/20 to-blue-500/10 border-indigo-500/20 hover:border-indigo-500/40' },
  { id: 'hormones', icon: Heart, title: "Women's Hormone Optimization", desc: 'HRT, Nuvella, and personalized hormone support', color: 'from-rose-500/20 to-pink-500/10 border-rose-500/20 hover:border-rose-500/40' },
];

const HomePage = () => {
  const navigate = useNavigate();
  
  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [learnMoreTopic, setLearnMoreTopic] = useState('general');

  // Helper to open learn more modal with specific topic
  const openLearnMore = (topic) => {
    setLearnMoreTopic(topic);
    setShowLearnMoreModal(true);
  };

  return (
    <div className="min-h-screen bg-[#020817] text-foreground">
      {/* Navigation Header */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:border-cyan-500/50 transition-all">
              <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
            </div>
            <span className="font-heading text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">Bryan 2.0</span>
          </Link>
          
          <nav className="flex items-center gap-4 sm:gap-6">
            <Link to="/stack-finder" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2" data-testid="nav-stacks-link">
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Stack Finder</span>
            </Link>
            <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2" data-testid="nav-blog-link">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Blog</span>
            </Link>
            <a href="sms:8016518363" className="text-slate-400 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-2" data-testid="nav-text-bryan">
              <MessageSquare className="h-4 w-4" />
              <span>Text Bryan</span>
            </a>
            <Button
              onClick={() => navigate('/forecast')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-6 py-2 text-sm font-bold shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all"
              data-testid="nav-cta-btn"
            >
              AI Forecast
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-12">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#22D3EE] opacity-10 blur-[120px] rounded-full"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block mb-6 px-4 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/5 backdrop-blur-sm">
              <span className="text-cyan-400 text-sm font-medium tracking-wide">AI Health Optimization Platform</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              Your Body Has More Than One Goal — <span className="text-[#22D3EE]">Your Program Should Too</span>
            </h1>
            
            <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Most programs focus on one thing. We build personalized optimization plans around what you actually want — weight loss, energy, recovery, sleep, hormones, and performance — then show you exactly what your 90 days could look like.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <Button
                onClick={() => navigate('/forecast')}
                className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-10 py-7 text-lg font-bold shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] transition-all hover:scale-105 h-auto"
                data-testid="hero-cta-forecast"
              >
                Build My AI Forecast
                <Sparkles className="ml-2 h-5 w-5" />
              </Button>
              
              <Button
                onClick={() => navigate('/stack-finder')}
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/10 text-white rounded-full px-10 py-7 text-lg font-bold transition-all h-auto"
                data-testid="hero-cta-stacks"
              >
                <Search className="mr-2 h-5 w-5" />
                Find My Stack
              </Button>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Personalized to your profile</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> No consultation required to see results</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Medically supervised programs</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Are You Optimizing For? */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">What Are You Optimizing For?</h2>
            <p className="text-slate-400 text-lg">Select a goal to build your personalized forecast</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GOAL_CARDS.map((card, idx) => (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => navigate(`/forecast?goal=${card.id}`)}
                className={`group text-left p-5 rounded-2xl border bg-gradient-to-br backdrop-blur-xl transition-all hover:scale-[1.02] ${card.color}`}
                data-testid={`goal-card-${card.id}`}
              >
                <card.icon className="h-7 w-7 text-white/80 mb-3" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-bold text-white mb-1">{card.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{card.desc}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section - Supporting the 2 CTAs */}
      <section className="pb-24 relative">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="backdrop-blur-xl bg-slate-950/60 border border-cyan-500/20 rounded-3xl p-8 sm:p-12"
          >
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-white">
                How the AI Forecast Works
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                3 minutes to a personalized optimization plan
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {[
                {
                  icon: Brain,
                  title: 'Profile Analysis',
                  description: 'AI evaluates your goals, health profile, and lifestyle'
                },
                {
                  icon: Target,
                  title: 'Smart Matching',
                  description: 'Matched to therapies and stacks that fit your needs'
                },
                {
                  icon: Activity,
                  title: '90-Day Projection',
                  description: 'Realistic timeline and milestones for your goals'
                },
                {
                  icon: Shield,
                  title: 'Physician Guided',
                  description: 'Every recommendation backed by medical oversight'
                }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <item.icon className="h-6 w-6 text-[#22D3EE]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2 text-white">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => navigate('/forecast')}
              >
                <Brain className="mr-2 h-5 w-5" />
                Get My AI Forecast
              </Button>
              
              <span className="text-slate-500 text-sm">or</span>
              
              <a
                href="https://calendly.com/bryanm8623/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-full px-6 py-3 text-base font-medium transition-all"
                data-testid="value-cta-contact"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book My Free Call
              </a>
            </div>
            
            <p className="text-center text-xs text-slate-500 mt-4">
              5-minute assessment • Instant results • No commitment
            </p>
            <p className="text-center text-xs text-slate-400 mt-2">
              Or text Bryan directly at <a href="sms:8016518363" className="text-cyan-400 hover:text-cyan-300 font-semibold">801-651-8363</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 text-lg">Three simple steps to your personalized forecast</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                step: '01',
                title: 'Take the AI Quiz',
                description: 'Answer personalized questions about your goals, challenges, and preferences.'
              },
              {
                icon: Activity,
                step: '02',
                title: 'Get Your Forecast',
                description: 'Our AI analyzes your profile and generates a personalized weight loss forecast.'
              },
              {
                icon: Target,
                step: '03',
                title: 'Explore Options',
                description: 'Discover provider-guided treatment options that align with your goals.'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40 p-8 hover:bg-slate-900/60 backdrop-blur-xl"
              >
                <div className="absolute top-4 right-4 text-6xl font-heading font-bold text-white/5">
                  {item.step}
                </div>
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-[#22D3EE]/10 flex items-center justify-center mb-6">
                    <item.icon className="h-7 w-7 text-[#22D3EE]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              Who This Is For
            </h2>
            <p className="text-slate-400 text-lg">Medical weight loss support for your unique journey</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Target, label: 'Weight Loss Goals' },
              { icon: Activity, label: 'Energy & Vitality' },
              { icon: CheckCircle2, label: 'Confidence Building' },
              { icon: Shield, label: 'Metabolic Support' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col items-center gap-4 p-6 rounded-xl bg-card border border-border backdrop-blur-xl"
              >
                <div className="w-12 h-12 rounded-full bg-[#22D3EE]/10 flex items-center justify-center">
                  <item.icon className="h-6 w-6 text-[#22D3EE]" strokeWidth={1.5} />
                </div>
                <p className="text-center font-medium">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: 'What is the AI Weight Loss Forecast?',
                a: 'Our AI analyzes your personal profile, goals, and challenges to generate a personalized forecast of your weight loss journey. It helps identify the best paths forward based on patterns from similar profiles.'
              },
              {
                q: 'Are these medications FDA approved?',
                a: 'Compounded medications are not FDA approved. All treatment options require provider review and approval. Individual results may vary.'
              },
              {
                q: 'How does the process work?',
                a: 'Take our AI quiz, receive your personalized forecast, then connect with providers to explore treatment options that may be right for you.'
              },
              {
                q: 'What medications are available?',
                a: 'We help you explore provider-guided options including Semaglutide and Tirzepatide, available in both injection and oral forms based on your preferences.'
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="rounded-xl border border-white/10 bg-slate-950/40 p-6 backdrop-blur-xl"
              >
                <h3 className="font-heading text-xl font-bold mb-3">{faq.q}</h3>
                <p className="text-slate-400 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/5 to-transparent"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
              Take the first step toward your weight loss goals with personalized AI guidance or connect directly with Bryan.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <Button
                onClick={() => navigate('/forecast')}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-10 py-7 text-lg font-bold shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:shadow-[0_0_40px_rgba(16,185,129,0.7)] transition-all hover:scale-105 h-auto"
                data-testid="final-cta-forecast"
              >
                <Brain className="mr-2 h-5 w-5" />
                Get My AI Forecast
              </Button>
              
              <a
                href="https://calendly.com/bryanm8623/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border-2 border-cyan-500/30 hover:border-cyan-500/50 bg-cyan-500/5 hover:bg-cyan-500/10 text-cyan-300 hover:text-cyan-200 rounded-full px-10 py-7 text-lg font-bold transition-all h-auto"
                data-testid="final-cta-contact"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book My Free Call
              </a>
            </div>
            
            <p className="text-slate-400 text-base mb-6">
              Or text Bryan directly at <a href="sms:8016518363" className="text-cyan-400 hover:text-cyan-300 font-semibold">801-651-8363</a>
            </p>
            
            {/* Supporting links - demoted visual priority */}
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
              <button 
                onClick={() => openLearnMore('semaglutide')}
                className="hover:text-cyan-400 transition-colors"
                data-testid="footer-semaglutide-btn"
              >
                About Semaglutide
              </button>
              <span className="text-slate-700">•</span>
              <button 
                onClick={() => openLearnMore('tirzepatide')}
                className="hover:text-cyan-400 transition-colors"
                data-testid="footer-tirzepatide-btn"
              >
                About Tirzepatide
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer & Footer */}
      <section className="pt-16 pb-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          {/* 4-Column Footer */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Programs</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/therapies/weight-loss" className="hover:text-cyan-400 transition-colors">Weight Loss</Link></li>
                <li><Link to="/therapies/energy" className="hover:text-cyan-400 transition-colors">Energy & Cellular Health</Link></li>
                <li><Link to="/therapies/recovery" className="hover:text-cyan-400 transition-colors">Recovery & Healing</Link></li>
                <li><Link to="/therapies/body-composition" className="hover:text-cyan-400 transition-colors">Body Composition</Link></li>
                <li><Link to="/therapies/cognitive" className="hover:text-cyan-400 transition-colors">Cognitive Performance</Link></li>
                <li><Link to="/therapies/sleep" className="hover:text-cyan-400 transition-colors">Sleep Optimization</Link></li>
                <li><Link to="/therapies/hormones" className="hover:text-cyan-400 transition-colors">Women's Hormones</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Tools</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/forecast" className="hover:text-cyan-400 transition-colors">AI Forecast</Link></li>
                <li><Link to="/compare" className="hover:text-cyan-400 transition-colors">Compare Therapies</Link></li>
                <li><Link to="/stack-finder" className="hover:text-cyan-400 transition-colors">Stack Finder</Link></li>
                <li><Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link></li>
                <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Bryan</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Learn</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link></li>
                <li><Link to="/forecast" className="hover:text-cyan-400 transition-colors">Start Here</Link></li>
                <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Bryan</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><a href="sms:8016518363" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Text: 801-651-8363</a></li>
                <li><a href="https://calendly.com/bryanm8623/30min" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Calendar className="h-3 w-3" /> Book a Call</a></li>
                <li><a href="mailto:Bryan@DirectMeds.com" className="hover:text-cyan-400 transition-colors">Bryan@DirectMeds.com</a></li>
              </ul>
            </div>
          </div>

          {/* Compliance */}
          <div className="text-center text-xs text-slate-600 border-t border-white/5 pt-8 space-y-3">
            <p>All information on this site is educational and does not constitute medical advice. Results vary based on individual health profile, adherence, and medical history. All programs include licensed medical provider oversight. Bryan Maldonado is an Account Management Specialist at DirectMeds.</p>
            <p>&copy; 2026 Bryan 2.0 Forecast. All rights reserved.</p>
          </div>
        </div>
      </section>

      {/* Modals */}
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        source="home_page"
      />
      
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        source="home_page"
      />
      
      <LearnMoreModal
        isOpen={showLearnMoreModal}
        onClose={() => setShowLearnMoreModal(false)}
        topic={learnMoreTopic}
        source="home_page"
      />
      <ExitIntentPopup />
    </div>
  );
};

export default HomePage;